import React, {useEffect, useState} from 'react';
import {InputText} from "primereact/inputtext";
import InputDate from "./input-date";
import persianDate from "persian-date";
import DatePicker, {DateObject} from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";
import persian_en from "react-date-object/locales/persian_en";
import {formatSeconds, parseDate, timeSince, timestampToDate} from "../utils";
import {InputTextarea} from "primereact/inputtextarea";

export const types= {
    "TEXT":"TEXT",
    "LONG_TEXT":"LONG_TEXT",
    "NUMBER":"NUMBER",
    "PERSIAN_DATE":"PERSIAN_DATE",
    "GREGORIAN_DATE":"GREGORIAN_DATE",
    "TIME_DURATION":"TIME_DURATION",
    "TIME_SINCE":"TIME_SINCE",
}

export function normalizeRenderableDateValue(value) {
    const numericValue = typeof value === "number" || (typeof value === "string" && /^\d+$/.test(value.trim()))
        ? Number(value)
        : NaN;
    return Number.isFinite(numericValue) && numericValue > 0 && numericValue < 100000000000
        ? numericValue * 1000
        : value;
}

export function isRenderableDate(value) {
    if (value === null || value === undefined || value === "" || value === 0 || value === "0") return false;
    const date = new Date(normalizeRenderableDateValue(value));
    return Number.isFinite(date.getTime()) && date.getUTCFullYear() > 1971;
}

function Span({children, inputClassName, spanClassName, inputValue, onChange, onKeyDownEnter, onKeyDownEscape, focused, setFocused, placeholder, type, format}) {

    const [inputNewValue, setInputNewValue] = useState(inputValue);

    useEffect(()=>{
        setInputNewValue(inputValue);
    },[focused])

    const onFocusEnd = ()=>{
        onChange && onChange({value: inputNewValue});
        onKeyDownEnter && inputNewValue!==undefined && onKeyDownEnter({value:inputNewValue})
    }

    const Input = ()=>{
        switch (type) {

            case types.TEXT:return (
                <InputText placeholder={placeholder} className={`${inputClassName} p-1 outline-none shadow-none border-none`} value={inputNewValue} onChange={(e)=>{setInputNewValue(e.target.value)}} onKeyUp={(e)=>{
                    if (e.key==="Enter"){setFocused && setFocused(false); onFocusEnd();}
                    else if (e.key === "Escape"){
                        setFocused(false);
                        onKeyDownEscape && onKeyDownEscape();
                    }}} autoFocus onBlur={()=>{setFocused && setFocused(false);onFocusEnd()}}/>
            )

            case types.LONG_TEXT:return (
                <InputTextarea rows={4} placeholder={placeholder} className={`${inputClassName} p-1 outline-none shadow-none border-none`} value={inputNewValue} onChange={(e)=>{setInputNewValue(e.target.value)}} onKeyUp={(e)=>{
                    if (e.key === "Escape"){
                        setFocused?.(false);
                        onKeyDownEscape?.();
                    }}} autoFocus onBlur={()=>{setFocused?.(false);onFocusEnd()}}/>
            )

            case types.NUMBER:return (
                <InputText keyfilter="num" placeholder={placeholder} className={`${inputClassName} p-1 outline-none shadow-none border-none`} value={inputNewValue} onChange={(e)=>{setInputNewValue(e.target.value)}} onKeyUp={(e)=>{
                    if (e.key==="Enter"){setFocused && setFocused(false); onFocusEnd();}
                    else if (e.key === "Escape"){
                        setFocused(false);
                        onKeyDownEscape && onKeyDownEscape();
                    }}} autoFocus onBlur={()=>{setFocused && setFocused(false);onFocusEnd()}}/>
            )

            case types.PERSIAN_DATE:return (
                <DatePicker value={inputValue? new DateObject(inputValue*1000):new DateObject()} onClose={()=>{setFocused && setFocused(false);}} onChange={(e)=>{onChange && onChange({value: e.toUnix()});}} containerClassName={inputClassName} render={<InputDate autoFocus placeholder={placeholder} className={`${inputClassName} p-1  outline-none shadow-none border-none`} />} calendar={persian} locale={persian_fa} calendarPosition="bottom-right"/>
            )

            case types.GREGORIAN_DATE:return (
                <DatePicker className="english-font" value={inputValue? new DateObject(inputValue*1000):new DateObject()} onClose={()=>{setFocused && setFocused(false);}} onChange={(e)=>{onChange && onChange({value: e.toUnix()});}} containerClassName={inputClassName} render={<InputDate autoFocus placeholder={placeholder} className={`${inputClassName} english-font p-1 outline-none shadow-none border-none`} />}  calendarPosition="bottom-right"/>
            )

            default: return(
                <InputText placeholder={placeholder} className={`${inputClassName} p-1 outline-none shadow-none border-none`} value={inputNewValue} onChange={(e)=>{setInputNewValue(e.target.value)}} onKeyUp={(e)=>{
                if (e.key==="Enter"){setFocused && setFocused(false); onFocusEnd();}
                else if (e.key === "Escape"){
                    setFocused(false);
                    onKeyDownEscape && onKeyDownEscape();
                }}} autoFocus onBlur={()=>{setFocused && setFocused(false);onFocusEnd()}}/>)
        }
    }

    const render = ()=>{

        switch (type) {
            case types.TIME_SINCE:return <span className={`${!isRenderableDate(children)&&"text-500"}`}>{isRenderableDate(children)?timeSince(new Date(normalizeRenderableDateValue(children)).getTime()/1000):(placeholder ?? "---")}</span> ;
            case types.PERSIAN_DATE:return <span className={`${!isRenderableDate(children)&&"text-500"}`}>{isRenderableDate(children)?parseDate(normalizeRenderableDateValue(children), 'iso', format ? format : "YYYY/MM/DD HH:mm", 'persian', 'fa'):(placeholder ?? "---")}</span> ;
            case types.GREGORIAN_DATE:return <span className={`english-font ${!isRenderableDate(children)&&"text-500"}`}>{isRenderableDate(children)?parseDate(normalizeRenderableDateValue(children), 'iso', format ? format : "YYYY/MM/DD HH:mm", 'gregorian', 'en'):(placeholder ?? "---")}</span>;
            case types.NUMBER:return (+children).toLocaleString();
            case types.TIME_DURATION:return formatSeconds(+children);
            default:return children;
        }
    }

    return (
        <>
            {
                focused?(Input()):(<span data-text={children} className={spanClassName} onClick={()=>{setFocused && setFocused(true)}}>{render()}</span>)
            }
        </>
    );
}


//demo
/**
 *     const [edit, setEdit] = useState(false);
 *     const [value, setValue] = useState();
 *     return (
 *         <div className="p-3">
 *             <Span type={types.PERSIAN_DATE} focused={edit} setFocused={setEdit} inputValue={value} onChange={(e)=>{setValue(e.value);}} onKeyDownEscape={()=>{setEdit(false)}}>{value}</Span>
 *         </div>
 *     );
 */


export default Span;